// Month — a 7-column grid, each day one line: count + $. Click a day → that day's
// board. The month plan (Auto-Fill → Review → Approve → Finalize → push to
// FieldRoutes) keeps every step; its controls sit in the one-row header, its
// constraints and insights open as dialogs from ⋯. On the phone the grid becomes
// a list of days and a day opens a sheet.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ChevronRight } from "lucide-react";
import {
  previewMonthFill,
  getMonthPlan,
  advanceMonthPlan,
  rePushMonthPlan,
  getMonthPlanIssues,
  getMonthPlanProjections,
  getExistingCounts,
  getMonthSummary,
} from "../../../api/schedulingApi";
import useIsPhone from "../../../hooks/useIsPhone";
import Sheet from "../../ui/Sheet";
import Button from "../../ui/button";
import SchedHeader, { SchedDialog } from "../sched/SchedHeader";
import { formatCurrency } from "./utils";
import { getDispatchVocab } from "./dispatchVocab";
import { todayIso } from "../sched/useRangeBoard";
import "../RoutePlanner.css";
import "./dispatch-b.css";
import "../sched/sched-c.css";

const WD = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const ST_LABEL = { draft: "Draft", review: "In review", approved: "Approved", finalized: "Finalized" };

const ms = (y, m) => `${y}-${String(m + 1).padStart(2, "0")}`;
const pm = (s) => { const [y, m] = s.split("-").map(Number); return new Date(y, m - 1, 1); };
const dim = (y, m) => new Date(y, m + 1, 0).getDate();
const fmt = (n) => (n || 0).toLocaleString();
const err = (e, f) => e?.response?.data?.detail || f;

export default function MonthPlanView() {
  const navigate = useNavigate();
  const isPhone = useIsPhone();
  const vocab = useMemo(() => getDispatchVocab(), []);
  const goToDay = useCallback((ds) => { if (ds) navigate(`/schedule/day?date=${ds}`); }, [navigate]);
  const [monthStr, setMonthStr] = useState(() => ms(new Date().getFullYear(), new Date().getMonth()));
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(""); // "" | "generating" | "optimizing"
  const [dialog, setDialog] = useState(""); // "" | "constraints" | "insights" | "finalize"
  const [sheetDay, setSheetDay] = useState(null);
  const [constraints, setConstraints] = useState({ due_date_deviation_days: 7, max_jobs_per_tech_per_day: 10 });
  const [weights, setWeights] = useState({ tech_familiarity: 0.35, due_date_fit: 0.25, route_density: 0.25, capacity_balance: 0.15 });
  const [issues, setIssues] = useState([]);
  const [projections, setProjections] = useState(null);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null); // {date: {count, completed, revenue, booked_minutes, capacity_minutes}}
  const [loading, setLoading] = useState(true);
  const [push, setPush] = useState({ status: null, progress: null, error: null });

  const md = useMemo(() => pm(monthStr), [monthStr]);
  const yr = md.getFullYear();
  const mo = md.getMonth();
  const total = dim(yr, mo);
  const pad = (new Date(yr, mo, 1).getDay() || 7) - 1;
  const today = todayIso();

  // Converged per-day content: FieldRoutes + native appointments, the same
  // source as the day board. Falls back to the FR-only counts.
  useEffect(() => {
    let cancelled = false;
    setSummary(null); setLoading(true);
    getMonthSummary(monthStr)
      .then(({ data }) => { if (!cancelled) setSummary(data?.days || {}); })
      .catch(() => getExistingCounts(monthStr)
        .then(({ data }) => {
          if (cancelled) return;
          const out = {};
          Object.entries(data?.counts || {}).forEach(([ds, count]) => { out[ds] = { count }; });
          setSummary(out);
        })
        .catch(() => { if (!cancelled) setSummary({}); }))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [monthStr]);

  const loadMeta = useCallback(async (pid) => {
    try {
      const [{ data: p }, { data: i }, { data: pr }] = await Promise.all([getMonthPlan(pid), getMonthPlanIssues(pid), getMonthPlanProjections(pid)]);
      setPlan(p); setIssues(i?.issues || []); setProjections(pr?.projections || null); setError(null);
      setPush({ status: p?.push_status || null, progress: p?.push_progress || null, error: p?.push_error || null });
    } catch (e) { setError(err(e, "Could not load the month plan.")); }
  }, []);

  const fill = useCallback(async (optimizeExisting) => {
    setBusy(optimizeExisting ? "optimizing" : "generating"); setError(null);
    try {
      const { data } = await previewMonthFill(monthStr, optimizeExisting ? {} : constraints, optimizeExisting ? {} : weights, optimizeExisting);
      setPlan(data);
      if (data.plan_id) {
        const [{ data: i }, { data: pr }] = await Promise.all([getMonthPlanIssues(data.plan_id), getMonthPlanProjections(data.plan_id)]);
        setIssues(i?.issues || []); setProjections(pr?.projections || null);
      }
    } catch (e) { setError(err(e, optimizeExisting ? "Optimize failed." : "Auto-Fill failed.")); }
    finally { setBusy(""); }
  }, [monthStr, constraints, weights]);

  const advance = useCallback(async (to) => {
    if (!plan?.plan_id) return;
    try {
      const { data, status } = await advanceMonthPlan(plan.plan_id, to);
      if (to === "finalized" && (status === 202 || data?.push_status)) {
        setPush((p) => ({ ...p, status: data.push_status || "queued" }));
        setPlan((prev) => (prev ? { ...prev, state: "finalized" } : prev));
      } else await loadMeta(plan.plan_id);
    } catch (e) { setError(err(e, "Could not move the plan to the next step.")); }
  }, [plan, loadMeta]);

  const rePush = useCallback(async () => {
    if (!plan?.plan_id) return;
    try { const { data } = await rePushMonthPlan(plan.plan_id); setPush({ status: data.push_status || "queued", progress: null, error: null }); }
    catch (e) { setError(err(e, "Re-push failed.")); }
  }, [plan]);

  // Poll while a push to FieldRoutes runs.
  useEffect(() => {
    if (!plan?.plan_id || !["queued", "in_progress"].includes(push.status)) return undefined;
    const t = setInterval(async () => {
      try {
        const { data: p } = await getMonthPlan(plan.plan_id);
        setPush({ status: p.push_status || null, progress: p.push_progress || null, error: p.push_error || null });
        if (["completed", "failed"].includes(p.push_status)) setPlan(p);
      } catch { /* keep polling */ }
    }, 5000);
    return () => clearInterval(t);
  }, [plan?.plan_id, push.status]);

  const shift = useCallback((d) => {
    const x = pm(monthStr); x.setMonth(x.getMonth() + d);
    setMonthStr(ms(x.getFullYear(), x.getMonth())); setPlan(null); setIssues([]); setProjections(null);
  }, [monthStr]);

  const cells = useMemo(() => Array.from({ length: total }, (_, i) => {
    const d = i + 1;
    const ds = `${yr}-${String(mo + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    const real = (summary || {})[ds] || {};
    const cap = Number(real.capacity_minutes || 0);
    return {
      d, ds,
      count: Number(real.count || 0), completed: Number(real.completed || 0), revenue: Number(real.revenue || 0),
      booked: Number(real.booked_minutes || 0), cap, util: cap > 0 ? Math.round((Number(real.booked_minutes || 0) / cap) * 100) : null,
      planned: Number(plan?.day_summaries?.[ds]?.total_jobs || 0),
      issues: (issues || []).filter((x) => x.date === ds),
    };
  }), [summary, plan, issues, total, yr, mo]);

  const monthTotals = useMemo(() => cells.reduce((a, c) => ({ count: a.count + c.count, revenue: a.revenue + c.revenue }), { count: 0, revenue: 0 }), [cells]);
  const state = plan?.state;
  const st = plan?.stats || {};
  const label = md.toLocaleString(undefined, { month: "long", year: "numeric" });

  const actions = (
    <>
      {(!state || state === "draft") ? (
        <button type="button" className="dispatch-button secondary" onClick={() => fill(false)} disabled={Boolean(busy)}>
          {busy === "generating" ? "Filling…" : plan ? "Re-fill" : "Auto-Fill"}
        </button>
      ) : null}
      {state === "draft" && plan ? (
        <>
          <button type="button" className="dispatch-button secondary" onClick={() => fill(true)} disabled={Boolean(busy)}>{busy === "optimizing" ? "Optimizing…" : "Optimize"}</button>
          <button type="button" className="dispatch-button secondary" onClick={() => advance("review")}>Send to review</button>
        </>
      ) : null}
      {state === "review" ? (
        <>
          <button type="button" className="dispatch-button secondary" onClick={() => advance("draft")}>Back to draft</button>
          <button type="button" className="dispatch-button secondary" onClick={() => advance("approved")}>Approve</button>
        </>
      ) : null}
      {state === "approved" ? (
        <>
          <button type="button" className="dispatch-button secondary" onClick={() => advance("review")}>Reopen</button>
          <button type="button" className="dispatch-button secondary" onClick={() => setDialog("finalize")}>Finalize</button>
        </>
      ) : null}
      {state === "finalized" && !["queued", "in_progress"].includes(push.status) ? (
        <button type="button" className="dispatch-button secondary" onClick={rePush}>Re-push to FieldRoutes</button>
      ) : null}
    </>
  );

  const note = ["queued", "in_progress"].includes(push.status)
    ? `Pushing to FieldRoutes${push.progress?.fieldroutes_sync ? ` · ${push.progress.fieldroutes_sync.synced || 0}/${push.progress.fieldroutes_sync.total || "?"}` : "…"}`
    : push.status === "completed" ? `Pushed · ${push.progress?.committed || 0} committed`
    : push.status === "failed" ? `Push failed${push.error ? ` — ${push.error}` : ""}`
    : state ? `Plan: ${ST_LABEL[state] || state}${st.assigned != null ? ` · ${fmt(st.assigned)}/${fmt(st.total_jobs)} placed` : ""}`
    : null;

  const body = isPhone ? (
    <div className="sc-list" aria-label={`${label} days`}>
      {cells.filter((c) => c.count || c.planned || c.issues.length || c.ds === today).map((c) => (
        <button key={c.ds} type="button" className={`sc-row sc-row--day${c.ds === today ? " is-today" : ""}`} onClick={() => setSheetDay(c)}>
          <span className="sc-row-main">
            <b>{new Date(`${c.ds}T12:00:00`).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}</b>
            <span>{c.count ? `${c.count} ${vocab.stops}` : `No ${vocab.stops}`}{c.planned ? ` · +${c.planned} planned` : ""}{c.issues.length ? ` · ${c.issues.length} issue${c.issues.length === 1 ? "" : "s"}` : ""}</span>
          </span>
          <span className="sc-row-value">{c.revenue ? formatCurrency(c.revenue) : ""}</span>
          <ChevronRight size={14} className="sc-row-chev" aria-hidden="true" />
        </button>
      ))}
      {!loading && !cells.some((c) => c.count || c.planned) ? <div className="sc-loading">Nothing booked in {label}. Auto-Fill plans the month from due service plans.</div> : null}
    </div>
  ) : (
    <div className="sc-month" role="grid" aria-label={label}>
      {WD.map((d) => <div key={d} className="sc-month-wd" role="columnheader">{d}</div>)}
      {Array.from({ length: pad }).map((_, i) => <div key={`p${i}`} className="sc-month-day is-pad" aria-hidden="true" />)}
      {cells.map((c) => (
        <button
          key={c.ds}
          type="button"
          role="gridcell"
          className={`sc-month-day${c.ds === today ? " is-today" : ""}`}
          onClick={() => goToDay(c.ds)}
          title={`${c.count} ${vocab.stops}${c.completed ? ` · ${c.completed} done` : ""}${c.util != null ? ` · ${c.util}% booked` : ""} — open the day board`}
        >
          <span className="sc-month-line"><span className="sc-month-num">{c.d}</span>{c.issues.length ? <span className="sc-month-warn" title={`${c.issues.length} plan issue(s)`}><AlertTriangle size={11} /></span> : null}</span>
          {c.count || c.planned ? (
            <span className="sc-month-line">
              {c.count ? <span>{c.count}</span> : null}
              {c.revenue ? <span className="sc-money">· {formatCurrency(c.revenue)}</span> : null}
              {c.planned ? <span className="sc-plan">+{c.planned}</span> : null}
            </span>
          ) : <span className="sc-month-line sc-dim">—</span>}
        </button>
      ))}
    </div>
  );

  const constraintsBody = (
    <div className="sc-form">
      <label className="dispatch-modal-field"><span>Due-date flex (days)</span>
        <input className="dispatch-modal-input" type="number" min={1} max={30} value={constraints.due_date_deviation_days} onChange={(e) => setConstraints((p) => ({ ...p, due_date_deviation_days: Math.max(1, +e.target.value || 7) }))} />
      </label>
      <label className="dispatch-modal-field"><span>Most {vocab.stops} per {vocab.worker} per day</span>
        <input className="dispatch-modal-input" type="number" min={1} max={20} value={constraints.max_jobs_per_tech_per_day} onChange={(e) => setConstraints((p) => ({ ...p, max_jobs_per_tech_per_day: Math.max(1, +e.target.value || 10) }))} />
      </label>
      {[["tech_familiarity", `Same ${vocab.worker} as last visit`], ["due_date_fit", "On the due date"], ["route_density", "Tight routes"], ["capacity_balance", "Even days"]].map(([k, l]) => (
        <label key={k} className="dispatch-modal-field"><span>{l} · {Math.round(weights[k] * 100)}%</span>
          <input type="range" min={0} max={100} value={Math.round(weights[k] * 100)} onChange={(e) => setWeights((p) => ({ ...p, [k]: +e.target.value / 100 }))} />
        </label>
      ))}
      <p className="dispatch-modal-note">Weights total {Math.round(Object.values(weights).reduce((a, b) => a + b, 0) * 100)}% — Auto-Fill uses these on the next run.</p>
    </div>
  );

  const un = plan?.unassignable || [];
  const insightsBody = !plan ? (
    <p className="dispatch-modal-note">No plan for {label} yet. Auto-Fill builds one from the service plans due this month.</p>
  ) : (
    <div className="sc-facts-wrap">
      <dl className="sc-facts">
        <dt>Placed</dt><dd>{fmt(st.assigned)} of {fmt(st.total_jobs)}</dd>
        <dt>Same {vocab.worker}</dt><dd>{st.familiar_tech_rate ?? "—"}%</dd>
        <dt>Avg score</dt><dd>{st.avg_score ?? "—"}</dd>
        <dt>{vocab.workers ? vocab.workers.charAt(0).toUpperCase() + vocab.workers.slice(1) : "Techs"}</dt><dd>{st.tech_count ?? "—"} · {st.working_days ?? "—"} working days</dd>
        <dt>Unplaced</dt><dd>{un.length}</dd>
        <dt>Issues</dt><dd>{issues.length}</dd>
        <dt>Next month</dt><dd>{projections?.count || 0} due</dd>
      </dl>
      {un.length ? (
        <div className="sc-sheet-list">
          {un.slice(0, 25).map((u, i) => (
            <div key={i} className="sc-sheet-tech"><b>{u.customer_name}</b><span>{u.service_type} · {String(u.reason || "").replace(/_/g, " ")}</span></div>
          ))}
        </div>
      ) : null}
      {issues.length ? (
        <div className="sc-sheet-list">
          {issues.slice(0, 20).map((iss, i) => (
            <button key={i} type="button" className="sc-sheet-tech sc-link-row" onClick={() => iss.date && goToDay(iss.date)}><b>{iss.date}</b><span>{iss.type}{iss.tech ? ` · ${iss.tech}` : ""}</span></button>
          ))}
        </div>
      ) : null}
    </div>
  );

  return (
    <div className="dispatch-shell sc-screen">
      <SchedHeader
        label={label}
        onPrev={() => shift(-1)}
        onNext={() => shift(1)}
        prevLabel="Previous month"
        nextLabel="Next month"
        totals={summary ? [`${fmt(monthTotals.count)} ${vocab.stops}`, formatCurrency(monthTotals.revenue)] : []}
        actions={actions}
        menu={[
          (!state || state === "draft") ? { label: "Auto-Fill rules", run: () => setDialog("constraints") } : null,
          { label: "Plan insights", run: () => setDialog("insights") },
          { label: "This month", run: () => { setMonthStr(ms(new Date().getFullYear(), new Date().getMonth())); setPlan(null); } },
        ]}
        note={note}
      />
      {error ? <div className="sc-inline-error" role="alert">{error}</div> : null}
      {loading && !summary ? <div className="sc-loading">Loading {label}…</div> : body}

      {dialog === "constraints" ? (
        isPhone ? <Sheet open title="Auto-Fill rules" onClose={() => setDialog("")}>{constraintsBody}</Sheet>
          : <SchedDialog title="Auto-Fill rules" kicker={label} onClose={() => setDialog("")}>{constraintsBody}</SchedDialog>
      ) : null}
      {dialog === "insights" ? (
        isPhone ? <Sheet open title="Plan insights" onClose={() => setDialog("")}>{insightsBody}</Sheet>
          : <SchedDialog title="Plan insights" kicker={label} onClose={() => setDialog("")}>{insightsBody}</SchedDialog>
      ) : null}
      {dialog === "finalize" ? (
        <SchedDialog
          title="Finalize the month?"
          kicker={label}
          onClose={() => setDialog("")}
          footer={<><Button variant="secondary" onClick={() => setDialog("")}>Cancel</Button><Button variant="primary" onClick={() => { setDialog(""); advance("finalized"); }}>Finalize {fmt(st.assigned)} {vocab.stops}</Button></>}
        >
          <p className="dispatch-modal-note">This commits {fmt(st.assigned)} placed {vocab.stops} to the schedule and pushes them to FieldRoutes. It cannot be undone from here.</p>
        </SchedDialog>
      ) : null}

      {sheetDay ? (
        <Sheet
          open
          title={new Date(`${sheetDay.ds}T12:00:00`).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
          onClose={() => setSheetDay(null)}
          footer={<Button variant="primary" onClick={() => goToDay(sheetDay.ds)}>Open the day board</Button>}
        >
          <dl className="sc-facts">
            <dt>Booked</dt><dd>{sheetDay.count} {vocab.stops}{sheetDay.completed ? ` · ${sheetDay.completed} done` : ""}</dd>
            <dt>Value</dt><dd>{sheetDay.revenue ? formatCurrency(sheetDay.revenue) : "—"}</dd>
            <dt>Hours</dt><dd>{sheetDay.cap ? `${(sheetDay.booked / 60).toFixed(1)} of ${(sheetDay.cap / 60).toFixed(0)} h · ${sheetDay.util}%` : "—"}</dd>
            {sheetDay.planned ? <><dt>Planned</dt><dd>+{sheetDay.planned} from the month plan</dd></> : null}
            {sheetDay.issues.length ? <><dt>Issues</dt><dd>{sheetDay.issues.map((x) => x.type).join(", ")}</dd></> : null}
          </dl>
        </Sheet>
      ) : null}
    </div>
  );
}
